import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './MyRequirements.css';

const STATUS_COLOR = {
  open: '#108a00',
  in_progress: '#9a6700',
  completed: '#0969da',
  closed: '#6b7280',
};

const COPY = {
  zh: {
    kicker: '个人工作区',
    title: '我的需求',
    subtitle: '这里只显示你自己创建的需求，不包含需求大厅里的其他公开需求。',
    post: '发布需求',
    searchPlaceholder: '搜索我的需求名称、编号或描述',
    search: '搜索',
    status: '状态',
    statuses: [
      { value: 'all', label: '全部' },
      { value: 'open', label: '成果征集中' },
      { value: 'in_progress', label: '协议签订中' },
      { value: 'completed', label: '已完成' },
      { value: 'closed', label: '已关闭' },
    ],
    resultCount: count => `${count} 个我的需求`,
    loading: '正在加载我的需求...',
    loadError: '我的需求加载失败，请刷新重试',
    emptyTitle: '你还没有创建需求',
    emptyText: '创建需求后，只会在这里显示。',
    view: '查看详情',
    budget: '预算',
    submissions: count => `${count} 份投稿`,
    views: count => `${count} 次浏览`,
    statusLabels: {
      open: '成果征集中',
      in_progress: '协议签订中',
      completed: '已完成',
      closed: '已关闭',
    },
  },
  en: {
    kicker: 'Personal workspace',
    title: 'My requirements',
    subtitle: 'Only requirements created by your account appear here. Public requests belong in the requirement hall.',
    post: 'Post requirement',
    searchPlaceholder: 'Search my requirement name, ID, or description',
    search: 'Search',
    status: 'Status',
    statuses: [
      { value: 'all', label: 'All' },
      { value: 'open', label: 'Open submissions' },
      { value: 'in_progress', label: 'Contracting' },
      { value: 'completed', label: 'Completed' },
      { value: 'closed', label: 'Closed' },
    ],
    resultCount: count => `${count} of my requirements`,
    loading: 'Loading your requirements...',
    loadError: 'Your requirements could not be loaded. Refresh and try again.',
    emptyTitle: 'You have not created any requirements',
    emptyText: 'Requirements created by your account will appear here.',
    view: 'View details',
    budget: 'Budget',
    submissions: count => `${count} submissions`,
    views: count => `${count} views`,
    statusLabels: {
      open: 'Open for submissions',
      in_progress: 'Contracting',
      completed: 'Completed',
      closed: 'Closed',
    },
  },
};

function formatDate(value, language) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatBudget(value) {
  return Number(value || 0).toLocaleString();
}

export default function MyRequirements() {
  const { language, translatePhrase } = useLanguage();
  const text = COPY[language] || COPY.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const status = searchParams.get('status') || 'all';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    api.get('/tasks/mine', {
      params: { keyword: searchParams.get('keyword') || '', status },
    })
      .then(response => {
        if (active) setTasks(Array.isArray(response.data.tasks) ? response.data.tasks : []);
      })
      .catch(() => {
        if (!active) return;
        setTasks([]);
        setLoadError(text.loadError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [searchParams, status, text.loadError]);

  const updateSearch = event => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set('keyword', keyword.trim());
    else next.delete('keyword');
    setSearchParams(next);
  };

  const updateStatus = event => {
    const next = new URLSearchParams(searchParams);
    if (event.target.value === 'all') next.delete('status');
    else next.set('status', event.target.value);
    setSearchParams(next);
  };

  return (
    <div className="my-requirements-page">
      <div className="container my-requirements-shell">
        <header className="my-requirements-hero">
          <div>
            <div className="my-requirements-kicker">{text.kicker}</div>
            <h1>{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
          <Link to="/tasks/create" className="my-requirements-primary">{text.post}</Link>
        </header>

        <form className="my-requirements-toolbar" onSubmit={updateSearch}>
          <input
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            placeholder={text.searchPlaceholder}
          />
          <label>
            <span>{text.status}</span>
            <select value={status} onChange={updateStatus}>
              {text.statuses.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <button type="submit">{text.search}</button>
        </form>

        <div className="my-requirements-result-bar">
          <strong>{text.resultCount(tasks.length)}</strong>
        </div>

        {loading ? (
          <div className="my-requirements-state">{text.loading}</div>
        ) : loadError ? (
          <div className="my-requirements-state my-requirements-error">{loadError}</div>
        ) : tasks.length === 0 ? (
          <div className="my-requirements-state">
            <h2>{text.emptyTitle}</h2>
            <p>{text.emptyText}</p>
            <Link to="/tasks/create" className="my-requirements-primary">{text.post}</Link>
          </div>
        ) : (
          <section className="my-requirements-list" aria-label={text.title}>
            {tasks.map(task => (
              <Link key={task.id} to={`/tasks/${task.id}`} className="my-requirement-card">
                <div className="my-requirement-card-top">
                  <div>
                    <span
                      className="my-requirement-status"
                      style={{
                        color: STATUS_COLOR[task.status] || STATUS_COLOR.closed,
                        background: `${STATUS_COLOR[task.status] || STATUS_COLOR.closed}18`,
                      }}
                    >
                      {text.statusLabels[task.status] || task.status}
                    </span>
                    <span className="my-requirement-date">{formatDate(task.created_at, language)}</span>
                  </div>
                  <span className="my-requirement-view">{text.view}</span>
                </div>
                <h2>{translatePhrase(task.title)}</h2>
                <p>{translatePhrase(task.description)}</p>
                <div className="my-requirement-meta">
                  <span>{translatePhrase(task.category)}</span>
                  <span>{text.submissions(task.submit_count || 0)}</span>
                  <span>{text.views(task.view_count || 0)}</span>
                  <span>{text.budget} ¥{formatBudget(task.budget_min)} - ¥{formatBudget(task.budget_max)}</span>
                </div>
              </Link>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
